import Box from "@mui/material/Box"
import { useLocation, useNavigate, useParams } from "@tanstack/react-router"
import type { FC } from "react"
import { useEffect, useMemo } from "react"
import { FormProvider } from "react-hook-form"

import { GoodsReceiptProvider } from "../../../contexts"
import { useGetGoodsReceipt, useGoodsReceiptDetailsTabs, useGoodsReceiptStatusRedirect } from "../../../hooks"
import { usePurchaseOrderSelectionDialog } from "../../Incoming/hooks"
import {
  EMPTY_DEFAULT_FORM_VALUES,
  useGetGoodsReceiptPalletRows,
  useSoftReceivedGoodsReceivedActions,
  useSoftReceivedPurchaseOrdersForm,
} from "../hooks"

import { InvalidSoftReceivedLotNumbersConfirmationDialog } from "./InvalidSoftReceivedLotNumbersConfirmationDialog"
import { MarkAsNotReceivedConfirmation } from "./MarkAsNotReceivedConfirmation"
import { PalletsMismatchConfirmationDialog } from "./PalletsMismatchConfirmationDialog"
import { SoftReceivedGoodsReceiptFormDialog } from "./SoftReceivedGoodsReceiptFormDialog"

import { Button } from "@/components/common/Button"
import { CancelDialog } from "@/components/common/Modals"
import { PermissionActionType } from "@/constants/permissions"
import { GoodsReceiptStatus } from "@/graphql/codegen/graphql"
import { BasicPathType, useAppView } from "@/hooks/useAppView"
import { useNavigationConfirmation } from "@/hooks/useNavigationConfirmation"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import {
  GoodsReceiptDetailsLayout,
  GoodsReceiptFileFormDialog,
  PurchaseOrderRemoveConfirmation,
  PurchaseOrderSelectionDialog,
} from "@/screens/WarehouseReceiving/components"
import { GoodReceiptActionType } from "@/screens/WarehouseReceiving/types"

export const SoftReceivedGoodsReceiptDetailsLayout: FC = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { isTabletView, getBasicPathFor } = useAppView()
  const { goodsReceiptId } = useParams({
    from: `${getBasicPathFor(BasicPathType.WAREHOUSE_RECEIVING)}/soft-received/$goodsReceiptId/_layout`,
  })

  const { hasPermissions } = usePermissionManager()
  const canMaintain = hasPermissions([PermissionActionType.WAREHOUSE_RECEIVING_MAINTENANCE])

  const { goodsReceipt, refetch } = useGetGoodsReceipt(goodsReceiptId)
  useGoodsReceiptStatusRedirect({ goodsReceipt, expectedStatus: GoodsReceiptStatus.SoftReceived })
  const { palletRows: goodsReceiptPalletRows, refetch: refetchPalletRows } =
    useGetGoodsReceiptPalletRows(goodsReceiptId)
  const { tabs, activeTab } = useGoodsReceiptDetailsTabs({
    goodsReceiptId,
    baseRoute: "soft-received",
  })

  useEffect(() => {
    if (isTabletView) {
      window.scrollTo(0, 0)
    }
  }, [isTabletView])

  const {
    methods,
    canSubmit,
    isCancelDialogOpen,
    isResettingRef,
    handleCancel,
    handleCloseCancelConfirmation,
    handleConfirmCancel,
    handleSave,
    handleReset,
  } = useSoftReceivedPurchaseOrdersForm({
    goodsReceipt,
    goodsReceiptPalletRows,
    onSuccess: () => {
      refetch()
      refetchPalletRows()
    },
  })

  const { watch } = methods
  const formValues = watch()

  const existingOrderIds = useMemo(
    () => goodsReceipt?.orders?.map((order) => order.orderId) ?? [],
    [goodsReceipt?.orders]
  )

  const goodsReceiptBasePath = `${getBasicPathFor(BasicPathType.WAREHOUSE_RECEIVING)}/soft-received/${goodsReceiptId}`

  const {
    isConfirmationOpen: isNavigationConfirmationOpen,
    handleConfirmNavigation,
    handleCancelNavigation,
  } = useNavigationConfirmation({
    shouldBlock: canSubmit,
    allowedPath: goodsReceiptBasePath,
    onConfirmNavigation: () => {
      handleReset(EMPTY_DEFAULT_FORM_VALUES)
    },
  })

  const {
    searchQuery,
    inputValue,
    selectedPurchaseOrders,
    purchaseOrders,
    isFetching,
    handleSearch,
    handleInputChange,
    handleSelect,
    reset,
  } = usePurchaseOrderSelectionDialog(existingOrderIds)

  const {
    actionState,
    purchaseOrderToRemove,
    handleDialogClose,
    handleEditGoodsReceipt,
    handleMarkAsNotReceived,
    handleUpload,
    handleAddPurchaseOrder,
    handleConfirmAddPurchaseOrder,
    handleRemovePurchaseOrder,
    handlePurchaseOrderRemoveClose,
    handlePurchaseOrderRemoveSuccess,
    handleFinalizeCheckIn,
    handleProceedToFinalizeCheckIn,
    handleConfirmMissingLots,
  } = useSoftReceivedGoodsReceivedActions({
    goodsReceipt,
    selectedPurchaseOrders,
    formValues,
    hasUnsavedChanges: canSubmit,
    addPurchaseOrderSuccessMessage: "Goods Receipt successfully updated.",
    onRefetch: refetch,
    onSave: handleSave,
  })

  useEffect(() => {
    if (goodsReceipt?.status === GoodsReceiptStatus.PreCheckedIn && !pathname.includes("finalize-check-in")) {
      navigate({
        to: `${getBasicPathFor(BasicPathType.WAREHOUSE_RECEIVING)}/soft-received/$goodsReceiptId/finalize-check-in`,
        params: { goodsReceiptId },
      })
    }
  }, [goodsReceipt, navigate, goodsReceiptId, pathname, getBasicPathFor])

  if (!goodsReceipt || goodsReceipt.status !== GoodsReceiptStatus.SoftReceived) {
    return null
  }

  const handleMarkAsNotReceivedAction = () => {
    handleMarkAsNotReceived(goodsReceipt)
  }

  const headerActions = canMaintain ? (
    <Box className='flex items-center gap-2'>
      <Button appearance='outlined' onClick={handleCancel} data-testid='cancel-button'>
        Cancel
      </Button>
      <Button appearance='outlined' disabled={!canSubmit} onClick={handleSave} data-testid='save-button'>
        Save
      </Button>
      <Button appearance='contained' onClick={handleFinalizeCheckIn} data-testid='finalize-check-in-button'>
        Finalize Check In
      </Button>
    </Box>
  ) : undefined

  return (
    <GoodsReceiptProvider
      value={{
        goodsReceipt,
        isResettingRef,
        onFinalizeCheckIn: handleFinalizeCheckIn,
        onAddPurchaseOrder: handleAddPurchaseOrder,
        onRemovePurchaseOrder: handleRemovePurchaseOrder,
        onEdit: handleEditGoodsReceipt,
        onUpload: handleUpload,
        onMarkAsNotReceived: handleMarkAsNotReceivedAction,
      }}
    >
      <FormProvider {...methods}>
        <GoodsReceiptDetailsLayout
          goodsReceipt={goodsReceipt}
          tabs={tabs}
          activeTab={activeTab}
          backToPath={`${getBasicPathFor(BasicPathType.WAREHOUSE_RECEIVING)}/soft-received`}
          headerActions={headerActions}
          dataTestId='soft-received-goods-receipt-details'
          palletsCount={goodsReceipt.softReceiveData?.deliveredPalletsCount ?? 0}
        />
      </FormProvider>

      {isCancelDialogOpen && <CancelDialog onClose={handleCloseCancelConfirmation} onConfirm={handleConfirmCancel} />}

      {isNavigationConfirmationOpen && (
        <CancelDialog onClose={handleCancelNavigation} onConfirm={handleConfirmNavigation} />
      )}

      {actionState?.type === GoodReceiptActionType.Form && (
        <SoftReceivedGoodsReceiptFormDialog
          goodsReceipt={actionState.goodsReceipt!}
          onClose={handleDialogClose}
          onRefetch={refetch}
        />
      )}

      {actionState?.type === GoodReceiptActionType.MarkAsNotReceived && (
        <MarkAsNotReceivedConfirmation
          goodsReceipt={actionState.goodsReceipt}
          onClose={handleDialogClose}
          onSuccess={() => navigate({ to: `${getBasicPathFor(BasicPathType.WAREHOUSE_RECEIVING)}/incoming` })}
        />
      )}

      {actionState?.type === GoodReceiptActionType.FileUpload && (
        <GoodsReceiptFileFormDialog onClose={handleDialogClose} onRefresh={refetch} goodsReceiptId={goodsReceiptId} />
      )}

      {actionState?.type === GoodReceiptActionType.AddPurchaseOrder && (
        <PurchaseOrderSelectionDialog
          searchQuery={searchQuery}
          inputValue={inputValue}
          purchaseOrders={purchaseOrders}
          isFetching={isFetching}
          selectedPurchaseOrders={selectedPurchaseOrders}
          goodsReceipt={goodsReceipt}
          onSearch={handleSearch}
          onInputChange={handleInputChange}
          onSelect={handleSelect}
          onClose={() => {
            handleDialogClose()
            reset()
          }}
          onContinue={async () => {
            await handleConfirmAddPurchaseOrder()
            reset()
            refetch()
          }}
          isStandalone
        />
      )}

      {purchaseOrderToRemove !== null && goodsReceipt && (
        <PurchaseOrderRemoveConfirmation
          goodsReceipt={goodsReceipt}
          purchaseOrderId={purchaseOrderToRemove}
          onClose={handlePurchaseOrderRemoveClose}
          onSuccess={handlePurchaseOrderRemoveSuccess}
          successMessage='Purchase Order successfully removed.'
          customContent={`Are you sure you want to remove Purchase Order #${purchaseOrderToRemove} from this goods receipt?`}
        />
      )}

      {actionState?.type === GoodReceiptActionType.FinalizeCheckInMissingLots && (
        <InvalidSoftReceivedLotNumbersConfirmationDialog
          invalidLots={actionState.invalidLots}
          onClose={handleDialogClose}
          onConfirm={handleConfirmMissingLots}
        />
      )}

      {actionState?.type === GoodReceiptActionType.FinalizeCheckInConfirmPalletsCount && (
        <PalletsMismatchConfirmationDialog onClose={handleDialogClose} onConfirm={handleProceedToFinalizeCheckIn} />
      )}
    </GoodsReceiptProvider>
  )
}
